import * as React from 'react';
import {
  Autocomplete,
  Checkbox,
  Chip,
  ListItem,
  TextField,
} from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  BlueCheckboxCheckedIcon,
  CheckboxIcon,
  CircleDeleteIcon,
  GrayClose,
  SelectArrow,
} from '@/theme/overrides/CustomIcons';
import {
  AutocompleteHighlightColor,
  AutocompleteListItemStyle,
  AutocompletePaperStyle,
  AutocompleteSelectedText,
  AutocompleteSelectStyle,
  MuitlAutocompleteTextFieldStyle,
} from '@/components/AutoCompleteHighLightsSelect/styles';
import { WrappedAutoCompleteProps } from '@/components/AutoCompleteHighLightsSelect/common';

export default function MultiAutoCompleteHighLightsSelect<
  TOption,
  Multiple extends boolean | undefined = true,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  value,
  options,
  labelKey,
  valueKey,
  placeholder,
  helperText,
  error,
  optionSelectInputClear = true,
  ...props
}: WrappedAutoCompleteProps<TOption, Multiple, DisableClearable, FreeSolo>) {
  const optionValue = (option: string | TOption) =>
    typeof option === 'string' ? option : valueKey ? option[valueKey] + '' : '';
  const optionLabel = (option: string | TOption) =>
    typeof option === 'string' ? option : labelKey ? option[labelKey] + '' : '';
  const [inputValue, setInputValue] = React.useState('');

  return (
    <Autocomplete
      multiple={true as Multiple}
      disableCloseOnSelect={
        props.disableCloseOnSelect !== undefined
          ? props.disableCloseOnSelect
          : true
      }
      clearIcon={<CircleDeleteIcon />}
      clearOnBlur={false}
      options={options}
      value={value}
      popupIcon={
        <SelectArrow
          style={{
            height: '20px',
            width: '20px',
          }}
          stroke={props.disabled ? '#D1D8E2' : 'black'}
        />
      }
      slotProps={{
        paper: {
          elevation: 0,
          sx: AutocompletePaperStyle,
        },
      }}
      sx={{ ...AutocompleteSelectStyle, ...props.sx }}
      getOptionLabel={(option) => optionLabel(option)}
      inputValue={inputValue}
      onInputChange={(event, newInput, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInput);
        }
        if (optionSelectInputClear && reason == 'reset') {
          setInputValue(newInput);
        }
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              label={optionLabel(option)}
              key={key}
              sx={AutocompleteSelectedText}
              deleteIcon={<GrayClose />}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error ? error?.message : helperText ?? ''}
          placeholder={placeholder}
          margin="none"
          slotProps={{
            input: {
              ...params.InputProps,
              sx: {
                ...MuitlAutocompleteTextFieldStyle,
                '&.MuiOutlinedInput-root .MuiAutocomplete-input::placeholder': {
                  color:
                    Array.isArray(value) && value.length > 0
                      ? 'transparent'
                      : 'inherit',
                },
                '&.MuiOutlinedInput-root .MuiAutocomplete-input': {
                  width: Array.isArray(value) && value.length > 0 ? 0 : 'auto',
                },
              },
            },
          }}
        />
      )}
      renderOption={(props, option, { inputValue, selected }) => {
        const { key, ...optionProps } = props;
        const matches = match(optionLabel(option), inputValue ?? '', {
          insideWords: true,
        });
        const convertedOption = optionValue(option);
        const parts = parse(optionLabel(option), matches);
        return (
          <ListItem
            key={convertedOption}
            sx={AutocompleteListItemStyle}
            {...optionProps}
          >
            <Checkbox
              icon={<CheckboxIcon />}
              checkedIcon={<BlueCheckboxCheckedIcon />}
              style={{ marginRight: '6px', padding: 0 }}
              checked={selected}
            />
            {parts.map((part, index) => (
              <span
                key={index}
                style={{
                  whiteSpace: 'pre-wrap',
                  color:
                    selected && part.highlight
                      ? AutocompleteHighlightColor
                      : selected
                      ? 'black'
                      : part.highlight
                      ? AutocompleteHighlightColor
                      : 'black',
                }}
              >
                {part.text}
              </span>
            ))}
          </ListItem>
        );
      }}
      {...props}
    />
  );
}
